import { WellDetailsStates } from 'business/models/CustomModels';
import { DrawingLayer } from 'business/models/DrawingLayer';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import {
  GridLayerDTO,
  LayerDTO,
  LayerTypeDTO,
  PlateDropRegions,
  WellDropImportFlDTO,
} from 'business/models/Models';
import CaptureProfileService from 'business/services/CaptureProfileService';
import {
  getCroppedImages,
  getTunedImages,
  pasteModifiedCropsOnFullImage,
} from 'components/commandpanel/ImageTuning/utils';
import { GridLayerTypes } from 'components/commandpanel/Layers/LayersSelectionParent';
import { useModalHandlers } from 'components/modal';
import { wellImagesFetchedData } from 'components/plateviewer/well/WellImage/WellImage';
import { areWellDetailsEqual } from 'components/plateviewer/well/WellImage/utils';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  deleteLayers,
  getCompositeImageStoreImageByKey,
  setExpRegionCaptureProfiles,
  setIsLayerSelected,
  setIsUpdatingGlobalData,
} from 'redux/reducers/experimentDataReducer';
import {
  appendPreFetchedWellImages,
  callGenerateLayer,
  setAndAppendPlateDisabledImageRegions,
  setClearPreFetchedWellImages,
  setPreFetchedWellImages,
  setRemovePreFetchedWellImage,
  setReplacePreFetchedTunedImage,
  setToggleDisableImages,
} from 'redux/reducers/experimentReducer';
import { popDeletedLayers, setDeleteLayer } from 'redux/reducers/layerReducer';
import {
  toggleOverlaysVisibility,
  toggleScaleBarVisibility,
  toggleWellDropInfoPanelVisibility,
} from 'redux/reducers/wellReducer';
import {
  selectAllowLayerEditing,
  selectExp,
  selectExperimentPlates,
  selectIsUpdatingGlobalData,
  selectOriginalLayers,
} from 'redux/selectors/ExperimentStateSelector';
import { selectImportFl } from 'redux/selectors/StateSelectors';
import { updateWellDropImportFl } from 'redux/thunks/well';
import { getScoresOfWells } from 'redux/utils/experiment';
import { getIngredientColor, getLinkedLayers } from 'utils/Global';
import { WellIngredientDropInfoDetails } from 'components/Experiment/constants';
import { useWellDropImageMetaData } from './well';
import { getWellDropImageUniqueKey } from 'redux/utils/well';
import { getWellImageParams } from 'components/well/utils';

const overrideCompositeImageCache: Record<string, HTMLImageElement> = {};

export const useSelectedOverlay = () => {
  return useRMSelector((state) => state?.experiment.selectOverlay);
};

export const useSelectedDrawingOnCanvas = () => {
  return useRMSelector((state) => state?.plate.selectedDrawing);
};

export const useSetPreFetchedWellImages = (): ((
  imageData: wellImagesFetchedData,
) => void) => {
  const dispatch = useRMDispatch();
  const imageStack = cloneDeep(usePreFetchedWellImages());
  return (imageData: wellImagesFetchedData) => {
    const updatedImageStack = imageStack.filter(
      (x) => !areWellDetailsEqual(x.wellDetails, imageData.wellDetails),
    );
    updatedImageStack.push(imageData);
    dispatch(setPreFetchedWellImages(updatedImageStack));
  };
};

export const useAppendPreFetchedWellImages = (): ((
  imageData: wellImagesFetchedData,
) => void) => {
  const dispatch = useRMDispatch();
  return (imageData: wellImagesFetchedData) => {
    dispatch(appendPreFetchedWellImages(imageData));
  };
};
export const usePreFetchedWellImages = () => {
  return useRMSelector((state) => state?.plate?.preFetchedWellImages);
};

export const useAreAllAvailableImagesLoaded = (wellsList: WellDetailsStates[]) => {
  const imageKeys = useMemo(
    () => wellsList.map((well) => getWellDropImageUniqueKey(getWellImageParams(well))),
    [wellsList],
  );
  const imageStore = useRMSelector((state) => state?.well?.imageStore);
  return useMemo(() => {
    for (const imageKey of imageKeys) {
      const storeImage = imageStore?.[imageKey];
      if (storeImage && storeImage.fetchingStatus === EFetchingStatus.InProgress) {
        return false;
      }
    }
    return true;
  }, [imageKeys, imageStore]);
};

export const useCurrentWellDetailsImageStatus = (wellDetails) => {
  const preFetchedWellImages = usePreFetchedWellImages();
  return wellDetailImageStatus(preFetchedWellImages, wellDetails);
};

const wellDetailImageStatus = (preFetchedWellImages, wellDetails) => {
  return preFetchedWellImages.find((imagesFromStack) =>
    areWellDetailsEqual(wellDetails, imagesFromStack.wellDetails),
  )?.imageStatus;
};

export const useTuningAppliedWellNumbers = () => {
  const plates = useRMSelector(selectExperimentPlates);

  return (wellDetails) => {
    const plate = plates?.find((p) => p.plateNumber === wellDetails.plateNum);
    const wellNumbers = plate?.drawingIdentifiers?.plateHistograms
      ?.filter(
        (x) =>
          x.regionName === wellDetails.regionName &&
          x.dropNumber === wellDetails.dropNumber &&
          x.cpId === wellDetails.cpID &&
          x.imageBatchId === wellDetails.imageBatchId,
      )
      .map((x) => x.wellNumber);
    return wellNumbers;
  };
};

export const useClearPreFetchedWEllImages = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(setClearPreFetchedWellImages());
  };
};

export const useRemovePreFetchedWellImageData = (): ((
  dropRegionId: number,
  cpId: number,
  imageBatchId: number,
) => void) => {
  const dispatch = useRMDispatch();
  const wellImagesData = usePreFetchedWellImages();

  const getWellNumber = (
    dropRegionId: number,
    cpId: number,
    imageBatchId: number,
  ): number | undefined => {
    // Find the well number based on the provided dropRegionId, cpId, and imageBatchId
    const wellImageKeys = Object.keys(wellImagesData);
    for (const key of wellImageKeys) {
      const wellDetails = wellImagesData[key].data.wellDetails;
      if (
        wellDetails.regionID == dropRegionId &&
        wellDetails.cpID == cpId &&
        wellDetails.imgBatchId == imageBatchId
      ) {
        return wellDetails.wellNum;
      }
    }
    return undefined; // Well number not found
  };

  return (dropRegionId: number, cpId: number, imageBatchId: number) => {
    const wellNum = getWellNumber(dropRegionId, cpId, imageBatchId);
    dispatch(setRemovePreFetchedWellImage(wellNum));
  };
};

export const useReplaceWellTunedImageData = (): ((
  wellDetails: WellDetailsStates,
  tuningValues: ImageTuningValues,
  tunedImage: HTMLImageElement | { r: number; g: number; b: number },
) => void) => {
  const dispatch = useRMDispatch();
  return (
    wellDetails: WellDetailsStates,
    tuningValues: ImageTuningValues,
    tunedImage: HTMLImageElement | { r: number; g: number; b: number },
  ) => {
    dispatch(setReplacePreFetchedTunedImage({ wellDetails, tuningValues, tunedImage }));
  };
};

export const useIsDataInImageStack = (): ((
  wellDetails: WellDetailsStates,
) => boolean) => {
  const imageStack = usePreFetchedWellImages();
  return (wellDetails: WellDetailsStates) => {
    const filteredImages = imageStack?.filter(
      (x) =>
        x.wellDetails.wellNum === wellDetails.wellNum &&
        x.wellDetails.cpID === wellDetails.cpID &&
        x.wellDetails.dropNumber === wellDetails.dropNumber &&
        x.wellDetails.plateID === wellDetails.plateID &&
        x.wellDetails.regionName === wellDetails.regionName &&
        x.wellDetails.inspectionSet?.id === wellDetails.inspectionSet?.id,
    );
    return filteredImages.length > 0;
  };
};

export const useWellNumbersWithCurrentWellDetailsInImageStack = (): ((
  wellDetails: WellDetailsStates,
) => number[]) => {
  const imageStack = cloneDeep(usePreFetchedWellImages());
  return (wellDetails: WellDetailsStates) => {
    const wellNumbersWithCpId: number[] = imageStack
      ?.filter(
        (x) =>
          x.wellDetails.cpID === wellDetails.cpID &&
          x.wellDetails.dropNumber === wellDetails.dropNumber &&
          x.wellDetails.plateID === wellDetails.plateID &&
          x.wellDetails.regionName === wellDetails.regionName &&
          x.wellDetails.inspectionSet?.id === wellDetails.inspectionSet?.id,
      )
      .map((x) => x.wellDetails.wellNum);
    return wellNumbersWithCpId;
  };
};
export const useCPNameByCPId = (cpId) => {
  return useRMSelector((state) =>
    state?.experiment.expData.profiles?.length > 0
      ? state?.experiment?.expData?.profiles.find((cp) => cp.id == cpId)?.name
      : null,
  );
};

export const useInspectionSetByGroupBatchId = (groupBatchId) => {
  const inspections = useRMSelector((state) => state?.experiment?.expData?.inspections);
  return useMemo(
    () =>
      inspections?.length > 0
        ? inspections.find((insp) =>
            insp.groupedInspections.some((ginsp) => ginsp.id == groupBatchId),
          )
        : null,
    [inspections, groupBatchId],
  );
};

export const useTotalWellsInExperiment = () => {
  return useRMSelector((state) =>
    state?.experiment?.expData?.plateDto?.length > 0
      ? state?.experiment?.expData?.plateDto[0].cols *
        state?.experiment.expData.plateDto[0].rows *
        state?.experiment.expData.plateDto.length
      : 0,
  );
};

export const useAllPlates = () => {
  return useRMSelector((state) => state?.experiment?.expData?.plateDto);
};

export const useScoresForWells = (
  wellDetails: { wellNumber: number; plateId: number; dropNumber: number }[],
) => {
  return useRMSelector((state) => {
    if (!wellDetails) {
      return [];
    }
    return getScoresOfWells(wellDetails, state?.experiment?.expData?.plateDto);
  });
};

export const useAllScoredWells = () => {
  const allPlates = useAllPlates();
  return useMemo(() => {
    if (!allPlates?.length) {
      return [];
    }

    const scoredMap = new Map();

    allPlates.forEach((plate) => {
      plate.plateScores?.forEach((group) => {
        group?.forEach((score) => {
          const key = `${plate.plateId}_${score.wellNumber}_${score.dropNumber}_${score.scoreBatchId}`;
          if (!scoredMap.has(key)) {
            scoredMap.set(key, {
              plateId: plate.plateId,
              wellNumber: score.wellNumber,
              dropNumber: score.dropNumber,
              imageBatchId: score.scoreBatchId,
            });
          }
        });
      });
    });

    return Array.from(scoredMap.values());
  }, [allPlates]);
};

export function useExperimentContaienrId() {
  return useRMSelector((state) => state.experiment?.expData?.info?.containerId);
}

export const useAllLayers = (): DrawingLayer[] =>
  useRMSelector((state) => state?.experiment?.expData?.layers);

export const useLayerById = (layerId) => {
  const allLayers = useAllLayers();
  return allLayers?.find((layer) => layer.id === layerId);
};

export const useDeleteLayers = () => {
  const dispatch = useRMDispatch();
  const store = useStore();
  return (layerToDelete: LayerDTO, deleteFamily = false) => {
    const allLayers = store.getState().experiment?.expData?.layers;
    const linkedLayers = getLinkedLayers(allLayers, layerToDelete, true);
    if (deleteFamily) {
      linkedLayers.forEach((layer) => {
        dispatch(setDeleteLayer(layer));
      });
    }
    dispatch(deleteLayers({ layerToDelete, deleteFamily }));

    if (allLayers?.findIndex((lyr) => lyr.dirty == true) >= 0) {
      dispatch(callGenerateLayer());
    }
  };
};

export function useExperimentContainerId() {
  return useRMSelector((state) => state.experiment?.expData?.info?.containerId);
}

export const useLayerDeleteHandler = () => {
  const originalLayers = useRMSelector(selectOriginalLayers);
  const allLayers = useAllLayers();
  const dispatch = useRMDispatch();
  const preserveExperimentState = usePreserveExperiment();
  const { showModal, hideModal } = useModalHandlers();
  const deleteLayers = useDeleteLayers();
  return useCallback(
    (layerId, isSubGrid = true, showWarningModal = true) => {
      const tempLayer: LayerDTO =
        layerId > 0 && !isSubGrid && originalLayers?.length
          ? originalLayers.find((layer) => layer.id === layerId)
          : allLayers.find((layer) => layer.id === layerId);
      let layerType = null;
      if (
        !isSubGrid &&
        tempLayer.type == LayerTypeDTO.Grid &&
        allLayers.findIndex(
          (lyr) =>
            lyr.id !== tempLayer.id &&
            lyr.type == LayerTypeDTO.Grid &&
            (lyr.layerDetail as GridLayerDTO).gridId ==
              (tempLayer.layerDetail as GridLayerDTO)?.gridId,
        ) > -1
      ) {
        layerType = GridLayerTypes.SUBGRID;
      } else {
        layerType = tempLayer.type;
      }
      const isDeleteFamily: boolean = layerType == GridLayerTypes.SUBGRID;

      dispatch(setDeleteLayer(tempLayer));
      preserveExperimentState();
      dispatch(setIsLayerSelected(false));

      if (isDeleteFamily && showWarningModal) {
        showModal({
          heading: 'Delete Family',
          body: 'Are you sure want to delete family linked layer? \nThis action will delete all layers in the group.',
          onOk: () => {
            hideModal();
            deleteLayers(tempLayer, isDeleteFamily);
          },
          onCancel: () => {
            dispatch(popDeletedLayers());
            hideModal();
          },
        });
      } else {
        deleteLayers(tempLayer, isDeleteFamily);
      }
    },
    [originalLayers, allLayers, deleteLayers],
  );
};
export const useAnnotationPreference = () => {
  return useRMSelector((state) => state.plate?.annotationPreference);
};

export const usePlateDisabledImageRegions = () => {
  return useRMSelector((state) => state.plate?.plateDisabledImageRegions);
};

export const useSetAndAppendPlateDisabledImageRegions = (): ((
  plateDisabledImageRegions: PlateDropRegions[],
  plateIdToUpdate: number,
) => void) => {
  const dispatch = useRMDispatch();
  return (plateDisabledImageRegions: PlateDropRegions[], plateIdToUpdate: number) => {
    dispatch(
      setAndAppendPlateDisabledImageRegions({
        plateDisabledImageRegions,
        plateIdToUpdate,
      }),
    );
  };
};

export const useToggleDisableImages = (): ((
  selectedRegionIDs: number[],
  toggleState: boolean,
) => void) => {
  const dispatch = useRMDispatch();
  return (selectedRegionIDs: number[], toggleState: boolean) => {
    dispatch(setToggleDisableImages({ selectedRegionIDs, toggleState }));
  };
};

export const useShowScaleBar = (): [boolean | undefined, () => void] => {
  const dispatch = useRMDispatch();
  const showScaleBar = useRMSelector((state) => state.well.showScaleBar);
  return [showScaleBar, () => dispatch(toggleScaleBarVisibility())];
};

export const useShowOverlays = (): [boolean | undefined, () => void] => {
  const dispatch = useRMDispatch();
  const showOverlays = useRMSelector((state) => state.well.showOverlays);
  return [showOverlays, () => dispatch(toggleOverlaysVisibility())];
};

export const useShowWellDropInfoPanel = (): [boolean | undefined, () => void] => {
  const dispatch = useRMDispatch();
  const showWellInfoPanel = useRMSelector((state) => state.well.showWellDropInfoPanel);
  return [showWellInfoPanel, () => dispatch(toggleWellDropInfoPanelVisibility())];
};

export const useWellDropImportFlToggle = (): [boolean | undefined | null, () => void] => {
  const [cpImportFl, setCpImportFl] = useState<boolean>();
  const cpService = new CaptureProfileService();
  const dispatch = useRMDispatch();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, cpId } = selectedDropImageParameters || {};
  const importFl = useRMSelector((state) =>
    selectImportFl(state, plateID, wellNum, dropNumber),
  );
  const currentImportFl = importFl ?? cpImportFl;

  const params = {
    plateId: plateID,
    wellNumber: wellNum,
    dropNumber,
    importFl: !currentImportFl,
  } as WellDropImportFlDTO;

  useEffect(() => {
    const fetchData = async () => {
      return await cpService.getCaptureProfileById(cpId);
    };
    if (cpId) {
      fetchData().then((d) => {
        setCpImportFl(d?.importFl);
      });
    }
  }, [cpId]);

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  return useMemo(
    () => [currentImportFl, () => dispatch(updateWellDropImportFl(params))],
    [currentImportFl, wellNum, plateID, dropNumber, cpId],
  );
};

export const useUpdateExpRegionCaptureProfiles = () => {
  const dispatch = useRMDispatch();
  return (regionCaptureProfiles) => {
    dispatch(setExpRegionCaptureProfiles(regionCaptureProfiles));
  };
};
export const usePlateImageStructure = () => {
  return useRMSelector((state) => state?.experiment?.plateImageStructure);
};

export const useIsGlobalDataLoaded = () => {
  return useRMSelector((state) => state.plate.globaldataloaded);
};

export const useAllRegions = () => {
  return useRMSelector((state) => state?.experiment?.expData?.regions);
};

export const useAllWellNavigationDetails = () => {
  return useRMSelector(
    (state) => state?.experiment?.wellSelectionDetails?.wellNavigationDetails,
  );
};

export const useLastSelectedWellNavigationDetails = () => {
  return useRMSelector((state) => {
    const wellNavigationDetails =
      state?.experiment?.wellSelectionDetails?.wellNavigationDetails;
    return wellNavigationDetails?.[wellNavigationDetails?.length - 1];
  });
};

export const useAllDropImageParameters = () => {
  return useRMSelector(
    (state) => state?.experiment?.wellSelectionDetails?.dropImageParameters,
  );
};

export const useLastSelectedDropImageParameters = () => {
  return useRMSelector((state) => {
    const wellImagingSettingDetails =
      state?.experiment?.wellSelectionDetails?.dropImageParameters;
    return wellImagingSettingDetails?.[wellImagingSettingDetails?.length - 1];
  });
};

export const useWellSelectionDetails = () => {
  return useRMSelector((state) => state?.experiment?.wellSelectionDetails);
};

export const useSelectedDropImageParameters = () => {
  return useRMSelector(
    (state) => state?.experiment?.wellSelectionDetails?.selectedDropImageParameters,
  );
};

export const useImageTuningValues = () => {
  return useRMSelector((state) => state.plate.imageTuningValues);
};

/**
 * specifically handles case of screen layer removal,
 * with screen repair logic.
 * @returns [boolean, () => void]
 * ref: hooks/common.ts: useIsScreenIngredientEditable
 */
export const useIsLayerRemovalAllowed = (): boolean => {
  const screenRepairInfo = useRMSelector(
    (state) => state?.experiment?.expData?.screenRepairInfo,
  );
  if (screenRepairInfo && screenRepairInfo?.totalLayersUsingScreen > 0) {
    return false;
  }
  return true;
};

export const useAllInspections = () => {
  return useRMSelector((state) => state?.experiment?.expData?.inspections);
};

export const useExperimentRegions = () => {
  return useRMSelector((state) => state?.experiment?.expData?.regions);
};

export const useOverrideCompositeImage = () => {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const wellDropMetaDeta = useWellDropImageMetaData();
  const plates = useRMSelector(selectExperimentPlates);
  const getTuningAppliedWellNumbers = useTuningAppliedWellNumbers();
  const plateImageStructures = usePlateImageStructure();

  useEffect(() => {
    // Drop-level tuning updates should invalidate composed override image cache.
    Object.keys(overrideCompositeImageCache).forEach((key) => {
      delete overrideCompositeImageCache[key];
    });
  }, [wellDropMetaDeta]);

  return async (plateID) => {
    const { wellNum } = wellNavigationDetails || {};
    const { cpId, dropNumber, inspection, regionName } =
      selectedDropImageParameters || {};
    const plate = plates?.find((p) => p.plateId === plateID);
    const wellDetails = {
      wellNum,
      plateNum: plate.plateNumber,
      plateID: plateID,
      cpID: cpId,
      dropNumber,
      inspectionSet: inspection,
      regionName,
      imageBatchId: inspection?.groupedInspections?.find((gi) => gi.plateId === plateID)
        ?.imageBatchId,
    } as WellDetailsStates;

    const plateImageStructure = plateImageStructures?.find(
      (p) => p.plateNumber === wellDetails.plateNum,
    );
    const plateImageCompositeKey = plateImageStructure?.imageCompositeKeyString;
    const cacheKey = `${plateID}-${plateImageCompositeKey}-${cpId}-${dropNumber}-${regionName}-${wellDetails.imageBatchId}`;
    if (overrideCompositeImageCache[cacheKey]) {
      return overrideCompositeImageCache[cacheKey];
    }
    const plateCoordinates = {
      startX: plateImageStructure.startX,
      startY: plateImageStructure.startY,
      plateWidth: plateImageStructure.width,
      plateHeight: plateImageStructure.height,
    };
    const plateImage = getCompositeImageStoreImageByKey(plateImageCompositeKey);
    const tuningAppliedWellNumbers = getTuningAppliedWellNumbers(wellDetails);
    if (
      !plateImage ||
      plateImage == 'NoImage' ||
      !tuningAppliedWellNumbers ||
      tuningAppliedWellNumbers.length === 0
    ) {
      return plateImage;
    }
    const croppedImages = await getCroppedImages(
      plateImage,
      tuningAppliedWellNumbers,
      plate.wellRects,
      plateCoordinates,
    );
    const displaySize = {
      width: plateCoordinates.plateWidth,
      height: plateCoordinates.plateHeight,
      startX: plateCoordinates.startX,
      startY: plateCoordinates.startY,
    };
    const tunedImages = await getTunedImages(
      tuningAppliedWellNumbers,
      croppedImages,
      wellDropMetaDeta,
      wellDetails,
    );
    const finalFullImage = pasteModifiedCropsOnFullImage(
      tunedImages, // array of processed <img>
      plate.wellRects, // same cropFromDisplay used earlier
      displaySize, // same one from crop step
      plateImage, // original full image used during cropping
    );
    overrideCompositeImageCache[cacheKey] = finalFullImage;
    return finalFullImage;
  };
};

export const useAllowLayerEditing = (): boolean => {
  return useRMSelector(selectAllowLayerEditing);
};

export const useWellIngredientsAndDropInfo = (
  plateID: number,
  wellNumber: number,
  dropNumber: number,
): WellIngredientDropInfoDetails => {
  if (!plateID || !wellNumber || !dropNumber) {
    return;
  }

  const exp = useRMSelector(selectExp);
  const plate = exp?.plateDto?.find((p) => p.plateId === plateID);

  if (!plate) {
    return;
  }

  const well = plate.ingredients?.wellList?.find((w) => w.wellNumber === wellNumber);

  if (!well) {
    return;
  }

  const wellIngredients =
    well.wellLayerList?.flatMap((layer) =>
      layer.wellLayerIngredientList.map((wli) => ({
        conc: wli.concentration,
        concUnit: wli.ingredientStock?.concentrationUnits,
        shortName: wli.ingredientStockIngredient?.shortName,
        longName: wli.ingredientStockIngredient?.longName,
        pH: wli.pH,
        type: wli.ingredientTypeName,
        ingredientColor: getIngredientColor(wli.ingredientIngredientTypeId),
      })),
    ) ?? [];

  const drop = well.dropList?.find((d) => d.dropNumber === dropNumber);

  return {
    plateID: plate.plateId,
    plateBarcode: plate.barCode,
    wellName: well.rowLetter + well.columnNumber,
    dropNumber: drop.dropNumber,
    dropWellVolume: drop.dropWellVolume ?? 0,
    dropProteinVolume: drop.dropProteinVolume ?? 0,
    wellIngredients,
  };
};

export const useIsUpdatingGlobalData = () => {
  const dispatch = useRMDispatch();
  const updatingGlobalData = useRMSelector(selectIsUpdatingGlobalData);

  const setUpdatingGlobalData = (value: boolean) => {
    dispatch(setIsUpdatingGlobalData(value));
  };

  return { updatingGlobalData, setUpdatingGlobalData };
};

export const useScoreExperimentWellsInProgressCount = () => {
  return useRMSelector((state) => state?.experiment?.scoreExperimentWellsInProgressCount);
};

/**
 * @deprecated Legacy state; use useScoresForWells instead
 */
export const useCurrentWellScores = () => {
  return useRMSelector((state) => state?.experiment?.currentWellScores);
};

export const useAllIngredientStocksInExperiment = () => {
  return useRMSelector((state) => {
    const ingredientNames = new Set<string>();

    state?.experiment?.expData?.plateDto?.forEach((plate) =>
      plate.ingredients?.wellList?.forEach((well) =>
        well.wellLayerList?.forEach((wellLayer) =>
          wellLayer.wellLayerIngredientList?.forEach((ingredient) => {
            const name = ingredient.ingredientStockIngredient?.longName;
            if (name != null) {
              ingredientNames.add(name);
            }
          }),
        ),
      ),
    );

    return Array.from(ingredientNames);
  });
};
